import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getWeeklyReports,
  updateWeeklyReport,
  deleteWeeklyReport,
  addFeedback,
  getFeedbackByReportId,
} from '../../database/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import toast from 'react-hot-toast';
import {
  HiDocumentText,
  HiCheckCircle,
  HiXCircle,
  HiChatBubbleLeftEllipsis,
  HiDocument,
  HiClock,
  HiArrowDownTray,
  HiTrash,
  HiChevronLeft,
  HiChevronRight,
  HiUser,
  HiXMark,
  HiChevronDown,
} from 'react-icons/hi2';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const getStatusColor = status => {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const ClientReportsPopover = ({
  reports = [],
  onStatusUpdate,
  onDelete,
  onViewDetails,
  onAddFeedback,
  collectionName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!reports || reports.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50"
      >
        <span>{reports.length} Reports</span>
        <HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-6xl rounded-2xl bg-white/95 shadow-xl backdrop-blur-sm"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-100 p-6">
                <h3 className="text-lg font-medium text-black">Client Reports</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600"
                >
                  <HiXMark className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white/95 backdrop-blur-sm">
                    <tr className="border-b border-gray-100">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                        Files
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reports.map(report => (
                      <tr key={report.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <h3 className="truncate text-sm font-medium text-black">
                              {report.title}
                            </h3>
                            <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                              {report.content}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <HiClock className="h-4 w-4 text-gray-400" />
                            {new Date(report.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <HiDocument className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {report.fileData?.length || 0} files
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <HiCheckCircle className="h-3 w-3 text-green-600" />
                                {report.status === 'approved' ? 'Approved' : ''}
                              </span>
                              <span className="flex items-center gap-1">
                                <HiXCircle className="h-3 w-3 text-red-700" />
                                {report.status === 'rejected' ? 'Rejected' : ''}
                              </span>
                              <span className="flex items-center gap-1">
                                <HiClock className="h-3 w-3 text-gray-500" />
                                {report.status === 'pending' ? 'Pending' : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(report.status)}`}
                          >
                            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                onViewDetails(report);
                                setIsOpen(false);
                              }}
                              className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                            >
                              <HiDocumentText className="h-4 w-4" />
                              <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                View Details
                              </span>
                            </button>
                            {report.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    onStatusUpdate(report.id, 'approved', collectionName);
                                    setIsOpen(false);
                                  }}
                                  className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-green-100 hover:text-green-600"
                                >
                                  <HiCheckCircle className="h-4 w-4" />
                                  <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                    Approve
                                  </span>
                                </button>
                                <button
                                  onClick={() => {
                                    onStatusUpdate(report.id, 'rejected', collectionName);
                                    setIsOpen(false);
                                  }}
                                  className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-red-100 hover:text-red-600"
                                >
                                  <HiXCircle className="h-4 w-4" />
                                  <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                    Reject
                                  </span>
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                onAddFeedback(report);
                                setIsOpen(false);
                              }}
                              className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                            >
                              <HiChatBubbleLeftEllipsis className="h-4 w-4" />
                              <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                Add Feedback
                              </span>
                            </button>
                            <button
                              onClick={() => {
                                onDelete(report.id, collectionName);
                                setIsOpen(false);
                              }}
                              className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-red-50 hover:text-red-600"
                              title="Delete Report"
                            >
                              <HiTrash className="h-4 w-4" />
                              <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                Delete
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const itemsPerPage = 15;
  const [activeTab, setActiveTab] = useState('client');
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    reportId: null,
    collectionName: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadReports();
  }, [activeTab]);

  const loadReports = async () => {
    try {
      setLoading(true);
      let allReports = [];

      if (activeTab === 'client') {
        allReports = await getWeeklyReports();
      } else if (activeTab === 'moderator') {
        const moderatorReportsRef = collection(firestore, 'moderatorReports');
        const q = query(moderatorReportsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        allReports = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } else if (activeTab === 'alumni') {
        const alumniReportsRef = collection(firestore, 'alumniReports');
        const q = query(alumniReportsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        allReports = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } else if (activeTab === 'coaches') {
        const coachesReportsRef = collection(firestore, 'coachesReports');
        const q = query(coachesReportsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        allReports = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      const reportsByUser = allReports.reduce((acc, report) => {
        const userEmail = report.email;
        if (!acc[userEmail]) {
          acc[userEmail] = {
            email: userEmail,
            firstName: report.firstName || '',
            lastName: report.lastName || '',
            fullName: `${report.firstName || ''} ${report.lastName || ''}`.trim(),
            latestReport: report,
            reports: [report],
          };
        } else {
          acc[userEmail].reports.push(report);
          if (new Date(report.createdAt) > new Date(acc[userEmail].latestReport.createdAt)) {
            acc[userEmail].latestReport = report;
          }
        }
        return acc;
      }, {});
      const sortedUsers = Object.values(reportsByUser).sort(
        (a, b) => new Date(b.latestReport.createdAt) - new Date(a.latestReport.createdAt)
      );

      setReports(sortedUsers);
    } catch (error) {
      toast.error('Error loading reports:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (reportId, newStatus, collectionName) => {
    try {
      if (activeTab === 'client') {
        await updateWeeklyReport(reportId, { status: newStatus });
      } else {
        await updateDoc(doc(firestore, collectionName, reportId), { status: newStatus });
      }
      toast.success(`Report ${newStatus} successfully`);
      loadReports();
    } catch (error) {
      toast.error('Error updating report status:', error.message);
    }
  };

  const handleDeleteClick = (reportId, collectionName) => {
    setDeleteConfirm({
      isOpen: true,
      reportId,
      collectionName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.reportId || !deleteConfirm.collectionName) return;

    setIsDeleting(true);
    try {
      if (activeTab === 'client') {
        await deleteWeeklyReport(deleteConfirm.reportId);
      } else {
        await deleteDoc(doc(firestore, deleteConfirm.collectionName, deleteConfirm.reportId));
      }
      toast.success('Report deleted successfully');
      setDeleteConfirm({ isOpen: false, reportId: null, collectionName: null });
      loadReports();
    } catch (error) {
      toast.error(`Error deleting report: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, reportId: null, collectionName: null });
  };

  const handleFeedbackSubmit = async e => {
    e.preventDefault();
    if (!feedback.trim()) return;

    try {
      await addFeedback({
        reportId: selectedReport.id,
        content: feedback,
        userId: selectedReport.userId,
      });
      toast.success('Feedback added successfully');
      setFeedback('');
      setShowFeedbackForm(false);
      loadReports();
    } catch (error) {
      toast.error('Error adding feedback:', error.message);
    }
  };

  const totalPages = Math.ceil(reports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = reports.slice(startIndex, endIndex);

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="mb-2 text-2xl font-light text-black md:text-3xl">Reports</h1>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('client')}
              className={`rounded-lg px-6 py-2 font-medium transition-colors ${activeTab === 'client' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
            >
              Client Reports
            </button>
            <button
              onClick={() => setActiveTab('moderator')}
              className={`rounded-lg px-6 py-2 font-medium transition-colors ${activeTab === 'moderator' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
            >
              Business Advisor Reports
            </button>
            <button
              onClick={() => setActiveTab('alumni')}
              className={`rounded-lg px-6 py-2 font-medium transition-colors ${activeTab === 'alumni' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
            >
              Alumni Reports
            </button>
            <button
              onClick={() => setActiveTab('coaches')}
              className={`rounded-lg px-6 py-2 font-medium transition-colors ${activeTab === 'coaches' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
            >
              Coaches Reports
            </button>
          </div>
        </motion.div>
        <>
          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <div className="max-h-[70vh] overflow-y-auto md:max-h-[800px]">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-gray-50/50">
                    <tr className="border-b border-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        {activeTab === 'client'
                          ? 'Client'
                          : activeTab === 'moderator'
                            ? 'Moderator'
                            : activeTab === 'alumni'
                              ? 'Alumni'
                              : 'Coach'}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        Latest Report
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        Files
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentReports.length === 0 ? (
                      <motion.tr
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hover:bg-gray-50/50"
                      >
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <HiDocumentText className="h-12 w-12 text-gray-300" />
                            <h3 className="text-lg font-medium text-gray-900">No Reports Found</h3>
                            <p className="text-sm text-gray-500">
                              There are no reports to display at this time.
                            </p>
                          </div>
                        </td>
                      </motion.tr>
                    ) : (
                      currentReports.map((client, index) => (
                        <motion.tr
                          key={client.email}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="group hover:bg-gray-50/50"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                                <HiUser className="h-4 w-4 text-gray-600" />
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {client.fullName || 'Unknown'}
                                </span>
                                <p className="text-xs text-gray-500">{client.email}</p>
                                <p className="text-xs font-medium capitalize text-blue-600">
                                  {activeTab}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <h3 className="truncate text-sm font-medium text-gray-900">
                                {client.latestReport.title}
                              </h3>
                              <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                                {client.latestReport.content}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <HiClock className="h-4 w-4 text-gray-400" />
                              {new Date(client.latestReport.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <HiDocument className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {client.reports.reduce(
                                    (total, report) => total + (report.fileData?.length || 0),
                                    0
                                  )}{' '}
                                  total files
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <HiCheckCircle className="h-3 w-3 text-green-600" />
                                  {client.reports.filter(r => r.status === 'approved').length}{' '}
                                  approved
                                </span>
                                <span className="flex items-center gap-1">
                                  <HiXCircle className="h-3 w-3 text-red-700" />
                                  {client.reports.filter(r => r.status === 'rejected').length}{' '}
                                  rejected
                                </span>
                                <span className="flex items-center gap-1">
                                  <HiClock className="h-3 w-3 text-gray-500" />
                                  {client.reports.filter(r => r.status === 'pending').length}{' '}
                                  pending
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(client.latestReport.status)}`}
                            >
                              {client.latestReport.status.charAt(0).toUpperCase() +
                                client.latestReport.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {client.reports.length > 1 ? (
                                <ClientReportsPopover
                                  reports={client.reports}
                                  onStatusUpdate={handleStatusUpdate}
                                  onDelete={handleDeleteClick}
                                  onViewDetails={setSelectedReport}
                                  onAddFeedback={report => {
                                    setSelectedReport(report);
                                    setShowFeedbackForm(true);
                                  }}
                                  collectionName={
                                    activeTab === 'client'
                                      ? 'weeklyReports'
                                      : activeTab === 'moderator'
                                        ? 'moderatorReports'
                                        : activeTab === 'alumni'
                                          ? 'alumniReports'
                                          : 'coachesReports'
                                  }
                                />
                              ) : (
                                <>
                                  <button
                                    onClick={() => setSelectedReport(client.latestReport)}
                                    className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                  >
                                    <HiDocumentText className="h-4 w-4" />
                                    <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                      View Details
                                    </span>
                                  </button>
                                  {client.latestReport.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() =>
                                          handleStatusUpdate(
                                            client.latestReport.id,
                                            'approved',
                                            activeTab === 'client'
                                              ? 'weeklyReports'
                                              : activeTab === 'moderator'
                                                ? 'moderatorReports'
                                                : activeTab === 'alumni'
                                                  ? 'alumniReports'
                                                  : 'coachesReports'
                                          )
                                        }
                                        className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-green-100 hover:text-green-600"
                                      >
                                        <HiCheckCircle className="h-4 w-4" />
                                        <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                          Approve
                                        </span>
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleStatusUpdate(
                                            client.latestReport.id,
                                            'rejected',
                                            activeTab === 'client'
                                              ? 'weeklyReports'
                                              : activeTab === 'moderator'
                                                ? 'moderatorReports'
                                                : activeTab === 'alumni'
                                                  ? 'alumniReports'
                                                  : 'coachesReports'
                                          )
                                        }
                                        className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-red-100 hover:text-red-600"
                                      >
                                        <HiXCircle className="h-4 w-4" />
                                        <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                          Reject
                                        </span>
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => {
                                      setSelectedReport(client.latestReport);
                                      setShowFeedbackForm(true);
                                    }}
                                    className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                  >
                                    <HiChatBubbleLeftEllipsis className="h-4 w-4" />
                                    <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                      Add Feedback
                                    </span>
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteClick(
                                        client.latestReport.id,
                                        activeTab === 'client'
                                          ? 'weeklyReports'
                                          : activeTab === 'moderator'
                                            ? 'moderatorReports'
                                            : activeTab === 'alumni'
                                              ? 'alumniReports'
                                              : 'coachesReports'
                                      )
                                    }
                                    className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-red-100 hover:text-red-600"
                                    title="Delete Report"
                                  >
                                    <HiTrash className="h-4 w-4" />
                                    <span className="pointer-events-none absolute -top-12 left-1/2 z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
                                      Delete
                                    </span>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {reports.length > itemsPerPage && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 px-6 py-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Showing</span>
                    <span className="font-medium">{startIndex + 1}</span>
                    <span>to</span>
                    <span className="font-medium">{Math.min(endIndex, reports.length)}</span>
                    <span>of</span>
                    <span className="font-medium">{reports.length}</span>
                    <span>results</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <HiChevronLeft className="h-4 w-4" />
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-all ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                      <HiChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <AnimatePresence>
            {selectedReport && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={() => {
                  setSelectedReport(null);
                  setShowFeedbackForm(false);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="w-full max-w-4xl rounded-2xl bg-white shadow-xl"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900">Report Details</h3>
                    <button
                      onClick={() => {
                        setSelectedReport(null);
                        setShowFeedbackForm(false);
                      }}
                      className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600"
                    >
                      <HiXMark className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      <div>
                        <h4 className="mb-2 text-sm font-medium text-black">Report Content</h4>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                          <h5 className="mb-2 text-lg font-medium text-black">
                            {selectedReport.title}
                          </h5>
                          <p className="whitespace-pre-wrap text-gray-600">
                            {selectedReport.content}
                          </p>
                        </div>
                      </div>
                      {selectedReport.fileData?.length > 0 && (
                        <div>
                          <h4 className="mb-2 text-sm font-medium text-gray-900">Attached Files</h4>
                          <div className="space-y-2">
                            {selectedReport.fileData.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white">
                                    <HiDocument className="h-4 w-4 text-gray-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                  title="Download"
                                >
                                  <HiArrowDownTray className="h-4 w-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {showFeedbackForm && (
                        <div>
                          <h4 className="mb-2 text-sm font-medium text-gray-900">Add Feedback</h4>
                          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                            <textarea
                              value={feedback}
                              onChange={e => setFeedback(e.target.value)}
                              rows={4}
                              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-black transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
                              placeholder="Enter your feedback..."
                            />
                            <div className="flex justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => setShowFeedbackForm(false)}
                                className="rounded-xl px-6 py-3 text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-900"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={!feedback.trim()}
                                className="rounded-xl bg-blue-600 px-8 py-3 text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                              >
                                Submit Feedback
                              </button>
                            </div>
                          </form>
                        </div>
                      )}
                      {selectedReport.feedback && (
                        <div>
                          <h4 className="mb-2 text-sm font-medium text-gray-900">
                            Previous Feedback
                          </h4>
                          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                            <p className="whitespace-pre-wrap text-gray-600">
                              {selectedReport.feedback}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <ConfirmationModal
            isOpen={deleteConfirm.isOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Report"
            message="Are you sure you want to delete this report? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            confirmButtonColor="bg-red-600 hover:bg-red-700"
            isLoading={isDeleting}
          />
        </>
      </div>
    </div>
  );
};

export default AdminReports;
